import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SearchX } from "lucide-react";
import {
  PhotoGridItem,
  PhotoGridItemSkeleton,
} from "@/components/records/photo-grid-item/photo-grid-item";
import { outboxPlaceholderRecord } from "@/hooks/use-pending-records";
import { useRecordCells } from "@/hooks/use-record-cells";
import { usePinchColumns } from "./use-pinch-columns";
import type { RecordFilters } from "@/lib/query-keys";
import type { ProjectRecord } from "@/types/api";

const TARGET_ITEM_WIDTH = 225;
const MIN_COLUMN_COUNT = 3;
const GAP = 4;

function computeColumnCount(containerWidth: number): number {
  if (containerWidth <= 0) return MIN_COLUMN_COUNT;
  return Math.max(
    MIN_COLUMN_COUNT,
    Math.floor(containerWidth / TARGET_ITEM_WIDTH),
  );
}

interface PhotoGridProps {
  projectId: string;
  /** Id of the record open in the side panel, marked in the gallery. */
  activeRecordId?: string | null;

  filters?: RecordFilters;
  onRecordClick?: (record: ProjectRecord) => void;
  /** Whether the grid is in selection mode. Drives item indicators and
   * routes taps to `onToggleSelection` instead of `onRecordClick`. */
  isSelectionMode?: boolean;
  /** Set of selected record IDs. Membership decides each item's indicator
   * state. Using a Set in the parent is recommended for O(1) lookups, but
   * an array works when count is small. */
  selectedIds?: ReadonlyArray<string>;
  /** Toggle a record's selection. Called when the user taps a tile while
   * `isSelectionMode === true`. */
  onToggleSelection?: (record: ProjectRecord) => void;
  /** Long-press fired on a tile. Used by the records view to enter
   * selection mode and pre-select the long-pressed record per
   * [[selection-mode.spec]] US-001. */
  onLongPressRecord?: (record: ProjectRecord) => void;
  /** Called when the loaded record list changes (new page, filter change).
   * The records view uses this to publish `totalCount` to the selection
   * provider for the Select All FAB conditional. */
  onItemsChange?: (records: ProjectRecord[]) => void;
  /** Records to leave out of the grid — the report Add picker hides what the
   * report already holds. See [[photo-grid.spec]] § Excluded records. */
  excludeIds?: ReadonlySet<string>;
  /** Replaces "No photos found" when nothing is left to show. */
  emptyState?: ReactNode;
}

export function PhotoGrid({
  projectId,
  filters,
  onRecordClick,
  isSelectionMode = false,
  selectedIds,
  onToggleSelection,
  onLongPressRecord,
  onItemsChange,
  excludeIds,
  emptyState,
  activeRecordId = null,
}: PhotoGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const responsiveColumnCount = useMemo(
    () => computeColumnCount(containerWidth),
    [containerWidth],
  );
  const columnCount = usePinchColumns(scrollRef, responsiveColumnCount);
  const itemSize = useMemo(() => {
    if (containerWidth <= 0 || columnCount <= 0) return TARGET_ITEM_WIDTH;
    return (containerWidth - GAP * (columnCount - 1)) / columnCount;
  }, [containerWidth, columnCount]);

  // Records, outbox tiles, paging and the offline cap all come from the
  // shared source [[photo-list.spec]] reads too, so the two presentations can
  // never disagree about what a filter returns. See [[photo-grid.spec]] US-006.
  const {
    cells: loadedCells,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
    refetch,
    offlineCapReached,
    cachedCount,
    totalCount,
    isFilteredOffline,
    retryOutbox,
    appendStateFor,
  } = useRecordCells(projectId, filters, onItemsChange);

  // Excluded records are dropped before layout, so they leave no gap. Without
  // an exclusion this is the loaded list itself.
  const cells = useMemo(
    () =>
      excludeIds?.size
        ? loadedCells.filter(
            (cell) =>
              cell.kind !== "record" || !excludeIds.has(cell.record.id),
          )
        : loadedCells,
    [loadedCells, excludeIds],
  );
  // Everything loaded so far was excluded. The scroll trigger below reads
  // virtual rows, and there are none, so it would never ask for the page that
  // might have something to show — keep fetching behind the skeleton instead.
  const allExcludedSoFar =
    loadedCells.length > 0 && cells.length === 0 && hasNextPage;

  useEffect(() => {
    if (allExcludedSoFar && !isFetchingNextPage && !isError) fetchNextPage();
  }, [allExcludedSoFar, isFetchingNextPage, isError, fetchNextPage]);

  // Build a stable Set of selected IDs for O(1) membership checks per row.
  // useMemo so a re-render of the grid (eg. virtual scroll) doesn't rebuild
  // the set on every paint.
  const selectedIdSet = useMemo(
    () => new Set(selectedIds ?? []),
    [selectedIds],
  );

  // Row counts are over the merged cell list (outbox tiles + server
  // records), so pending tiles get their own rows ahead of the grid.
  const loadedRowCount = Math.ceil(cells.length / columnCount);
  const skeletonRowCount = isFetchingNextPage ? 1 : 0;
  const rowCount = loadedRowCount + skeletonRowCount;

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => itemSize + GAP,
    overscan: 3,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [itemSize, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastVirtualRow = virtualRows[virtualRows.length - 1];

  useEffect(() => {
    if (!lastVirtualRow) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (lastVirtualRow.index >= loadedRowCount - 3) {
      fetchNextPage();
    }
  }, [
    lastVirtualRow,
    hasNextPage,
    isFetchingNextPage,
    loadedRowCount,
    fetchNextPage,
  ]);

  if (isLoading || (allExcludedSoFar && !isError)) {
    const visibleRows =
      containerHeight > 0 && itemSize > 0
        ? Math.ceil((containerHeight + GAP) / (itemSize + GAP))
        : 4;
    const skeletonCount = Math.max(columnCount, 1) * visibleRows;
    return (
      <div
        ref={scrollRef}
        className="h-full w-full overflow-auto p-2"
        aria-busy="true"
      >
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${Math.max(columnCount, 1)}, minmax(0, 1fr))`,
            gap: `${GAP}px`,
          }}
        >
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <PhotoGridItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Gate the error fallback on the absence of cached data. TanStack Query
  // preserves last-good `data` when a refetch throws, so for a pinned project
  // opened offline the persister-restored records sit in `records` while
  // `isError` flips true from the failed on-mount refetch. Replacing them
  // with the error UI would hide the photos the user pinned for exactly this
  // scenario. See [[photo-grid.spec]] US-005 / Stale-with-refresh-error.
  if (isError && cells.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-4 text-sm text-muted-foreground">
            Failed to load records.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (cells.length === 0 && emptyState) {
    return (
      <div ref={scrollRef} className="h-full w-full">
        {emptyState}
      </div>
    );
  }

  if (cells.length === 0) {
    return (
      <div
        ref={scrollRef}
        className="flex h-full w-full flex-col items-center justify-center gap-3 p-8"
        data-testid="records-filtered-empty"
      >
        <SearchX className="h-12 w-12 text-muted-foreground/50" />
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No photos found
          </p>
          <p className="text-sm text-muted-foreground/70">
            Try adjusting your filters or date range
          </p>
          {isFilteredOffline && (
            <p className="text-sm text-muted-foreground/70">
              Only photos saved for offline use are searched — reconnect to
              filter all.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="h-full w-full overflow-auto p-2">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative",
          width: "100%",
        }}
      >
        {virtualRows.map((virtualRow) => {
          const rowStart = virtualRow.index * columnCount;
          return (
            <div
              key={virtualRow.key}
              className="grid"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                gap: `${GAP}px`,
                paddingBottom: `${GAP}px`,
              }}
            >
              {Array.from({ length: columnCount }).map((_, colIndex) => {
                const cellIndex = rowStart + colIndex;
                const cell = cells[cellIndex];
                if (cell) {
                  // Both kinds render via PhotoGridItem. An outbox cell
                  // passes its sync-state descriptor + a synthesized
                  // placeholder record; a server cell passes the record.
                  const record =
                    cell.kind === "record"
                      ? cell.record
                      : outboxPlaceholderRecord(cell.tile);
                  // Append sync state applies only to SYNCED server records:
                  // a creating (outbox) tile has no server id to append to.
                  const appendState = appendStateFor(cell);
                  return (
                    <PhotoGridItem
                      key={record.id}
                      record={record}
                      outbox={cell.kind === "outbox" ? cell.tile : undefined}
                      onRetryOutbox={retryOutbox}
                      appendState={appendState}
                      isSelectionMode={isSelectionMode}
                      isSelected={selectedIdSet.has(record.id)}
                      isActive={record.id === activeRecordId}
                      onLongPress={onLongPressRecord}
                      onClick={(r) => {
                        if (isSelectionMode) {
                          onToggleSelection?.(r);
                          return;
                        }
                        onRecordClick?.(r);
                      }}
                    />
                  );
                }
                if (virtualRow.index >= loadedRowCount && isFetchingNextPage) {
                  return <PhotoGridItemSkeleton key={`skeleton-${colIndex}`} />;
                }
                return <div key={`empty-${colIndex}`} />;
              })}
            </div>
          );
        })}
      </div>
      {offlineCapReached && totalCount > cachedCount && (
        <div
          className="flex items-center justify-center px-4 py-6 text-center text-xs text-muted-foreground"
          data-testid="records-offline-cap-footer"
        >
          {isFilteredOffline
            ? `Filtered the ${cachedCount} of ${totalCount} photos saved offline · reconnect to filter all`
            : `${cachedCount} of ${totalCount} photos available offline · reconnect to load more`}
        </div>
      )}
    </div>
  );
}
